import type { ArchetypeId, QuotaState } from "@casttolink/core";

/**
 * Same origin on the web, an absolute origin inside the Capacitor builds.
 * Set VITE_API_BASE at build time for the mobile bundles.
 */
const BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/+$/, "");

export function apiUrl(path: string) {
  return `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

export interface Capabilities {
  transcription: boolean;
  generation: boolean;
  model: string;
  speechModel: string;
  auth: { url: string; anonKey: string } | null;
}

export interface Account {
  id: string;
  email: string;
  plan: "free" | "pro";
  quota: QuotaState;
}

export interface GeneratedDraft {
  id: ArchetypeId;
  text: string;
  origin: "claude" | "local";
}

export interface GenerateResponse {
  posts: GeneratedDraft[];
  model: string;
  truncated: boolean;
  generationId?: string;
  usage?: { inputTokens: number; outputTokens: number };
  quota?: QuotaState;
}

/** The fleet error shape, with the quota attached when the refusal was a quota one. */
export interface ApiErrorBody {
  error: string;
  message: string;
  detail?: string | null;
  quota?: QuotaState;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || `Request failed (${status})`);
    this.status = status;
    this.body = body;
  }
}

async function parse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let payload: unknown = null;

  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    /* handled below */
  }

  if (!response.ok) {
    const body = (payload ?? {}) as ApiErrorBody;
    throw new ApiError(response.status, {
      error: body.error ?? "unknown",
      message: body.message ?? `Request failed (${response.status})`,
      quota: body.quota,
    });
  }

  return payload as T;
}

export async function fetchStatus(signal?: AbortSignal) {
  return parse<Capabilities>(await fetch(apiUrl("/status"), { signal }));
}

export async function fetchAccount(token: string, signal?: AbortSignal) {
  return parse<Account>(
    await fetch(apiUrl("/me"), { headers: { authorization: `Bearer ${token}` }, signal }),
  );
}

export interface GenerateRequest {
  transcript: string;
  tone: string;
  audience: string;
  length: string;
  sourceKind: "upload" | "url" | "paste";
  sourceRef?: string | null;
  durationSeconds?: number | null;
}

export interface Checkout {
  action: string;
  fields: Record<string, string>;
}

export async function startPayfastCheckout(token: string) {
  return parse<Checkout>(
    await fetch(apiUrl("/payments/payfast/checkout"), {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
    }),
  );
}

/** PayFast expects a form post, not a redirect, so one is built and submitted. */
export function submitCheckout(checkout: Checkout) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = checkout.action;

  for (const [name, value] of Object.entries(checkout.fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

export interface PaypalCheckout {
  approveUrl: string;
  subscriptionId: string;
}

export async function startPaypalCheckout(token: string) {
  return parse<PaypalCheckout>(
    await fetch(apiUrl("/payments/paypal/checkout"), {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
    }),
  );
}

export async function postGenerate(token: string, body: GenerateRequest) {
  return parse<GenerateResponse>(
    await fetch(apiUrl("/generate"), {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    }),
  );
}
